import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../../../data/services/AuthService";
import { queryClient } from "../../../lib/queryClient";
import { queryStorage } from "../../../lib/queryStorage";
import { announceSessionChanged } from "../../../lib/sessionBroadcast";
import { useAuthStore } from "../../../stores/useAuthStore";
import { useChartFilterStore } from "../../../stores/useChartFilterStore";
import { useOrganizationStore } from "../../../stores/useOrganizationStore";

export interface UseSignOutResult {
  // Ends the session everywhere (API, store, caches, other tabs) and
  // navigates to /sign-in. Never rejects — logout is best effort.
  signOut: () => Promise<void>;
}

export function useSignOut(): UseSignOutResult {
  const navigate = useNavigate();

  const signOut = useCallback(async (): Promise<void> => {
    try {
      // Cookie identifies the session — no local token to check or send.
      await AuthService.logout();
    } catch {
      // idempotent — proceed regardless
    }
    useAuthStore.getState().clearAuth();
    useOrganizationStore.getState().clearAll();
    useChartFilterStore.getState().resetAllFilters();
    try {
      await queryStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
    } catch {
      // A stale persisted cache is harmless once auth is cleared.
    }
    queryClient.clear();

    // The httpOnly refresh cookie this just invalidated is shared by the
    // whole browser — every other open tab was riding on the same session,
    // not a separate one of its own. Tell them now so they sign out
    // immediately instead of staying usable (and, on a shared computer,
    // visibly logged in) until their own access token happens to expire.
    announceSessionChanged({ type: "logout" });

    navigate("/sign-in");
  }, [navigate]);

  return { signOut };
}
